// When a sound plays. Pure, so the rules are tested apart from Web Audio.
//   - muted, or the event switched off: silence;
//   - one sound per fact: the same key (an attempt, an order, an alert) plays
//     once, however many paths report it;
//   - at most one per event in its gap, so a burst is one sound, not a roll;
//   - in a background tab only your own results sound (the press does not).
import type { SoundEvent, SoundSettings } from './settings'

export const SOUND_MIN_GAP_MS: Record<SoundEvent, number> = {
  click: 60,
  buy: 1500,
  sell: 1500,
  tradeFailure: 1500,
  limitFill: 1500,
  alert: 1500,
  tracked: 1500,
  withdrawal: 1500,
}

/** How long a played key is remembered (one fact never sounds twice). */
export const SOUND_KEY_TTL_MS = 10 * 60_000

export interface SoundPolicyState {
  lastAt: Partial<Record<SoundEvent, number>>
  keys: Map<string, number>
}

export function newSoundPolicyState(): SoundPolicyState {
  return { lastAt: {}, keys: new Map() }
}

export function shouldPlaySound(
  state: SoundPolicyState,
  settings: SoundSettings,
  event: SoundEvent,
  key: string | undefined,
  now: number,
  hidden: boolean
): boolean {
  if (settings.muted || !settings.enabled[event] || settings.volume <= 0) return false
  if (hidden && event === 'click') return false
  for (const [seen, at] of state.keys) {
    if (now - at > SOUND_KEY_TTL_MS) state.keys.delete(seen)
  }
  if (key && state.keys.has(`${event}:${key}`)) return false
  const last = state.lastAt[event]
  if (last !== undefined && now - last < SOUND_MIN_GAP_MS[event]) {
    // Still one fact: remember the key so a later report of it stays silent.
    if (key) state.keys.set(`${event}:${key}`, now)
    return false
  }
  state.lastAt[event] = now
  if (key) state.keys.set(`${event}:${key}`, now)
  return true
}
